import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  TextField,
  Alert,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useState } from "react";
import ModalFooter from "@/modules/common/components/modals/modal-footer";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import useFetchRanks from "@/modules/common/hooks/ranks-data.hook";
import SingleSelectSearch from "@/modules/common/components/input/single-autocomplete-search";
import { muiTheme } from "@/styles/theme";
import useFetchSubDepartments from "@/modules/common/hooks/subdepartments-data.hook";
import { useMutation } from "@tanstack/react-query";
import {
  checkTargetMultiplier,
  createTargetMultiplier,
} from "@/modules/targets/api/index.v2";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { getCurrentQuarter } from "@/modules/targets/utils";
import { showToast } from "@/modules/common/utils";
import { SubDepartment, TOAST_IDS, ToastType } from "@/modules/common/types";
import { Multiplier } from "@/modules/targets/types";
import { queryClient } from "@/queryClient";
import { useParams } from "react-router-dom";

interface FormInputs {
  subDepartment: string;
  rank: string;
  minDirectReports: string;
  targetMultiplier: string;
}

interface CheckMultiplierParams {
  subdepartmentUId: string;
  organizationUId: string;
  rankUId: string;
  percentage: number;
  from: Date;
  to: Date;
}

const CreateTargetsMultiplier = ({ onClose }: { onClose: () => void }) => {
  const [multiplierError, setMultiplierError] = useState<string | null>(null);

  const { control, handleSubmit, watch } = useForm<FormInputs>({
    defaultValues: {
      subDepartment: "",
      rank: "",
      minDirectReports: "",
      targetMultiplier: "",
    },
  });

  const { uid } = useParams();

  const {
    ranks,
    ranksLoading,
    searchTerm: ranksSearch,
    setSearchTerm: ranksSetSearch,
  } = useFetchRanks({
    organizationUId: uid,
  });

  const {
    subDepartments,
    subDepartmentsLoading,
    searchTerm: subDepartmentsSearch,
    setSearchTerm: subDepartmentsSetSearch,
  } = useFetchSubDepartments({
    organizationUId: uid,
  });

  const { isMobile } = useScreenSize();
  const { selectedOrganization } = useOrganization();
  const [checkMultiplierData, setCheckMultiplierData] = useState<any>(null);

  const { mutate: checkMultiplier, isLoading: isCheckingMultiplier } =
    useMutation({
      mutationFn: (params: CheckMultiplierParams) =>
        checkTargetMultiplier(params),
      onSuccess: (data) => {
        setCheckMultiplierData(data);
        setMultiplierError(null);
      },
      onError: (error: any) => {
        setMultiplierError(error?.message || "Failed to check multiplier");
      },
    });

  const { mutate: createMultiplier, isLoading: isCreating } = useMutation({
    mutationFn: (params: {
      subDepartmentUId: string;
      organizationUId: string;
      rankUId: string;
      count: number;
      percentage: number;
      from: Date;
      to: Date;
    }) => createTargetMultiplier(params),
    onSuccess: () => {
      showToast({
        type: ToastType.SUCCESS,
        message: "Multiplier created successfully",
        toastId: TOAST_IDS.CREATE_TARGET_MULTIPLIER_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      queryClient.invalidateQueries(["target-multipliers"]);
      onClose();
    },
    onError: (error: any) => {
      setMultiplierError(error?.message || "Failed to create multiplier");
      showToast({
        type: ToastType.ERROR,
        message: error?.message || "Failed to create multiplier",
        toastId: TOAST_IDS.CREATE_TARGET_MULTIPLIER_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const watchSubDepartment = watch("subDepartment");
  const watchRank = watch("rank");
  const watchTargetMultiplier = watch("targetMultiplier");

  const currentQuarter = getCurrentQuarter();

  const handleCheckMultiplier = () => {
    setMultiplierError(null);

    const targetMultiplierValue = parseFloat(watchTargetMultiplier);

    if (isNaN(targetMultiplierValue) && targetMultiplierValue < 0) {
      setMultiplierError("Please enter a valid target multiplier percentage");
      return;
    }

    if (!selectedOrganization?.uid) {
      setMultiplierError("Organization not selected");
      return;
    }

    const today = new Date();
    const nextYear = new Date();
    nextYear.setFullYear(today.getFullYear() + 1);

    const params: CheckMultiplierParams = {
      subdepartmentUId: watchSubDepartment,
      organizationUId: selectedOrganization.uid,
      rankUId: watchRank,
      percentage: targetMultiplierValue,
      from: currentQuarter.startDate,
      to: currentQuarter.endDate,
    };

    checkMultiplier(params);
  };

  const onSubmit = async (data: FormInputs) => {
    if (!selectedOrganization?.uid) {
      setMultiplierError("Organization not selected");
      return;
    }

    const params = {
      subDepartmentUId: data.subDepartment,
      organizationUId: selectedOrganization.uid,
      rankUId: data.rank,
      count: parseInt(data.minDirectReports),
      percentage: parseFloat(data.targetMultiplier),
      from: currentQuarter.startDate,
      to: currentQuarter.endDate,
    };

    createMultiplier(params);
  };

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : 800,
        bgcolor: "background.white",
        borderRadius: "8px",
        p: isMobile ? 2 : 3,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h5_bold" sx={{ color: "neutral.900", mb: 4 }}>
            New Target Multiplier
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 2,
              mb: 4,
            }}
          >
            <Controller
              name="subDepartment"
              control={control}
              rules={{ required: "Sub Department is required" }}
              render={({ field }) => (
                <Box>
                  <SingleSelectSearch
                    options={subDepartments.map(
                      (subDepartment: SubDepartment) => ({
                        value: subDepartment.uid,
                        label: subDepartment.name,
                      })
                    )}
                    size="medium"
                    isLoading={subDepartmentsLoading}
                    label="Sub Department"
                    onValueChange={(value) => field.onChange(value)}
                    searchValue={subDepartmentsSearch}
                    onSearchValueChange={subDepartmentsSetSearch}
                  />
                </Box>
              )}
            />

            <Controller
              name="rank"
              control={control}
              rules={{ required: "Rank is required" }}
              render={({ field }) => (
                <Box>
                  <SingleSelectSearch
                    options={ranks.map((rank) => ({
                      value: rank.uid,
                      label: rank.title,
                    }))}
                    size="medium"
                    isLoading={ranksLoading}
                    label="Rank"
                    onValueChange={(value) => field.onChange(value)}
                    searchValue={ranksSearch}
                    onSearchValueChange={ranksSetSearch}
                  />
                </Box>
              )}
            />

            <Controller
              name="targetMultiplier"
              control={control}
              rules={{
                required: "Target Multiplier is required",
                pattern: {
                  value: /^\d*\.?\d*$/,
                  message: "Please enter a valid number",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Target Multiplier %"
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e") {
                        e.preventDefault();
                      }
                    }}
                    error={!!error}
                    helperText={error?.message}
                    type="number"
                    inputProps={{ step: "0.1", min: "0" }}
                  />
                </Box>
              )}
            />

            <Controller
              name="minDirectReports"
              control={control}
              rules={{
                required: "Minimum Direct Reports is required",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Please enter a valid positive number",
                },
                min: {
                  value: 0,
                  message: "Value must be 0 or greater",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Minimum Direct Reports"
                    error={!!error}
                    helperText={error?.message}
                    type="number"
                    inputProps={{
                      min: "0",
                      step: "1",
                      onKeyDown: (e) => {
                        if (e.key === "-" || e.key === "e") {
                          e.preventDefault();
                        }
                      },
                    }}
                  />
                </Box>
              )}
            />
          </Box>

          {multiplierError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {multiplierError}
            </Alert>
          )}

          <Box
            sx={{
              mb: 2,
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5_bold" sx={{ color: "neutral.900" }}>
              Multiplier Agent Target
            </Typography>
            <Tooltip
              title={
                !watchRank || !watchSubDepartment || !watchTargetMultiplier
                  ? "Please fill in all required fields"
                  : "Check if the multiplier there is a multiplier for the selected criteria"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCheckMultiplier}
                  disabled={
                    !watchRank ||
                    !watchSubDepartment ||
                    !watchTargetMultiplier ||
                    isCheckingMultiplier
                  }
                  sx={{
                    textTransform: "none",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    ...muiTheme.typography.h5_med,
                  }}
                >
                  {isCheckingMultiplier ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Check Multiplier"
                  )}
                </Button>
              </span>
            </Tooltip>
          </Box>

          {checkMultiplierData?.data && (
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                }}
              >
                {checkMultiplierData?.data?.map(
                  (multiplier: Multiplier, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="h5_bold" sx={{ mb: 1 }}>
                        {new Date(multiplier.from).toLocaleString("default", {
                          month: "long",
                        })}
                      </Typography>
                      <Typography variant="h5_bold">
                        {multiplier.amount.toLocaleString()} EGP
                      </Typography>
                    </Box>
                  )
                )}

                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    borderLeft: "1px solid",
                    borderColor: "neutral.200",
                  }}
                >
                  <Typography variant="h5_bold">Total</Typography>
                  <Typography variant="h5_bold">
                    {checkMultiplierData.data
                      .reduce(
                        (
                          sum: number,
                          item: {
                            amount: number;
                          }
                        ) => sum + item.amount,
                        0
                      )
                      .toLocaleString()}{" "}
                    EGP
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Alert
            icon={<InfoIcon />}
            severity="warning"
            sx={{
              mb: 4,
              color: "warning.600",
              bgcolor: "warning.50",
              "& .MuiAlert-icon": {
                color: "warning.600",
              },
            }}
          >
            Please note that editing targets after creation is not allowed
          </Alert>

          <ModalFooter
            handleClose={onClose}
            handleConfirm={handleSubmit(onSubmit)}
            confirmText="Save"
            cancelText="Discard"
            isForm={true}
            isSubmitting={isCreating}
            disabledConfirmButton={isCreating}
          />
        </Box>
      </form>
    </Box>
  );
};

export default CreateTargetsMultiplier;
